import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Plus, MapPin, AlertCircle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const examTypes = [
  { value: 'all', label: 'All Examinations' },
  { value: 'CBEAM', label: 'CBEAM - Aesthetic Medicine' },
  { value: 'CBELM', label: 'CBELM - Longevity Medicine' },
  { value: 'CBERM', label: 'CBERM - Regenerative Medicine' },
  { value: 'CBEAD', label: 'CBEAD - Aesthetic Dentistry' },
  { value: 'CBENA', label: 'CBENA - Aesthetic Nursing' },
  { value: 'CBEAA', label: 'CBEAA - Clinical Aesthetic Assistance' },
];

export default function OsceTab({ 
  token, 
  allocatedCandidates, 
  schedules, 
  fetchSchedules 
}) {
  const [examTypeFilter, setExamTypeFilter] = useState('all');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(null);
  const [showExamCentersModal, setShowExamCentersModal] = useState(false);
  const [examCenters, setExamCenters] = useState([]);
  const [rescheduleData, setRescheduleData] = useState({ new_date: '', new_time: '', reason: '' });
  
  const [scheduleForm, setScheduleForm] = useState({
    candidate_id: '',
    candidate_name: '',
    candidate_email: '',
    exam_date: '',
    exam_time: '',
    location: '',
    notes: ''
  });

  const fetchExamCenters = async () => {
    try {
      const res = await axios.get(`${API}/examiner/exam-centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExamCenters(res.data);
    } catch (err) { console.error(err); }
  };

  const handleScheduleOSCE = async () => {
    if (!scheduleForm.candidate_id || !scheduleForm.exam_date || !scheduleForm.exam_time) {
      toast.error('Please select candidate, date and time');
      return;
    }
    try {
      await axios.post(`${API}/examiner/schedule-osce`, scheduleForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OSCE examination scheduled successfully');
      setShowScheduleForm(false);
      setScheduleForm({ candidate_id: '', candidate_name: '', candidate_email: '', exam_date: '', exam_time: '', location: '', notes: '' });
      fetchSchedules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule OSCE'); }
  };

  const handleReschedule = async () => {
    if (!showRescheduleModal || !rescheduleData.new_date || !rescheduleData.new_time) {
      toast.error('Please fill in new date and time');
      return;
    }
    try {
      await axios.post(`${API}/examiner/osces/${showRescheduleModal.id}/reschedule`, rescheduleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OSCE rescheduled successfully');
      setShowRescheduleModal(null);
      setRescheduleData({ new_date: '', new_time: '', reason: '' });
      fetchSchedules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule OSCE'); }
  };

  // Get pending candidates
  const scheduledCandidateIds = schedules.map(s => s.candidate_id);
  let pendingCandidates = allocatedCandidates.filter(c => {
    const candidateId = c.candidate?.id || c.user_id;
    return !scheduledCandidateIds.includes(candidateId);
  });

  // Apply filter
  if (examTypeFilter !== 'all') {
    pendingCandidates = pendingCandidates.filter(c => {
      const examValue = typeof c.examination === 'object' ? c.examination?.type || c.examination?.name : c.examination;
      return c.examination_type === examTypeFilter || 
        c.exam_type === examTypeFilter ||
        examValue === examTypeFilter;
    });
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">OSCE Examinations</h3>
          <p className="text-sm text-slate-500">Schedule and manage OSCE sessions for your allocated candidates</p>
        </div>
        <div className="flex gap-2">
          <Select value={examTypeFilter} onValueChange={setExamTypeFilter}>
            <SelectTrigger className="w-[200px] rounded-sm text-sm h-9">
              <SelectValue placeholder="Filter by exam type" />
            </SelectTrigger>
            <SelectContent>
              {examTypes.map(et => (
                <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            size="sm" 
            onClick={() => setShowScheduleForm(!showScheduleForm)} 
            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Schedule OSCE
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="rounded-sm"
            onClick={() => {
              fetchExamCenters();
              setShowExamCentersModal(true);
            }}
          >
            <MapPin className="h-4 w-4 mr-1" /> View Centers
          </Button>
        </div>
      </div>

      {/* Pending Candidates */}
      {pendingCandidates.length > 0 && (
        <Card className="imcac-card mb-4 border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <h4 className="font-semibold text-[#1B2A4A]">Candidates Pending OSCE Scheduling ({pendingCandidates.length})</h4>
            </div>
            <div className="space-y-2">
              {pendingCandidates.map(c => {
                const candidateId = c.candidate?.id || c.user_id;
                const candidateName = c.candidate?.name || c.full_name || 'Unknown';
                const candidateEmail = c.candidate?.email || c.email || '';
                const examType = c.examination_type || c.exam_type || 
                  (typeof c.examination === 'object' ? c.examination?.type || c.examination?.name : c.examination) || '';
                return (
                  <div key={candidateId} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-sm font-bold">
                        {candidateName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#1B2A4A]">{candidateName}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-500">{candidateEmail}</p>
                          {examType && <Badge variant="outline" className="text-xs h-5">{examType}</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                      onClick={() => {
                        setScheduleForm({
                          ...scheduleForm,
                          candidate_id: candidateId,
                          candidate_name: candidateName,
                          candidate_email: candidateEmail
                        });
                        setShowScheduleForm(true);
                      }}
                      data-testid={`schedule-osce-${candidateId}`}
                    >
                      <Calendar className="h-4 w-4 mr-1" /> Schedule Now
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Form */}
      {showScheduleForm && (
        <Card className="imcac-card mb-4">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">Schedule New OSCE Examination</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Candidate</Label>
                <Select
                  value={scheduleForm.candidate_id}
                  onValueChange={v => {
                    const candidate = allocatedCandidates.find(c => c.candidate?.id === v || c.user_id === v);
                    setScheduleForm({
                      ...scheduleForm,
                      candidate_id: v,
                      candidate_name: candidate?.candidate?.name || candidate?.full_name || '',
                      candidate_email: candidate?.candidate?.email || candidate?.email || ''
                    });
                  }}
                >
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue placeholder="Select candidate" /></SelectTrigger>
                  <SelectContent>
                    {allocatedCandidates.map(c => (
                      <SelectItem key={c.candidate?.id || c.user_id} value={c.candidate?.id || c.user_id}>
                        {c.candidate?.name || c.full_name} ({c.candidate?.email || c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Candidate Email</Label>
                <Input
                  value={scheduleForm.candidate_email}
                  onChange={e => setScheduleForm({...scheduleForm, candidate_email: e.target.value})}
                  placeholder="candidate@email.com"
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Exam Date</Label>
                <Input
                  type="date"
                  value={scheduleForm.exam_date}
                  onChange={e => setScheduleForm({...scheduleForm, exam_date: e.target.value})}
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Exam Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.exam_time}
                  onChange={e => setScheduleForm({...scheduleForm, exam_time: e.target.value})}
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Location</Label>
                <Input
                  value={scheduleForm.location}
                  onChange={e => setScheduleForm({...scheduleForm, location: e.target.value})}
                  placeholder="IMCAC Examination Center, City"
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes (Optional)</Label>
                <Textarea
                  value={scheduleForm.notes}
                  onChange={e => setScheduleForm({...scheduleForm, notes: e.target.value})}
                  placeholder="Any special instructions..."
                  className="rounded-sm mt-1 text-sm"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleScheduleOSCE} className="bg-[#C5A572] text-white rounded-sm text-sm">
                <Calendar className="h-4 w-4 mr-1" /> Schedule & Notify
              </Button>
              <Button variant="outline" onClick={() => setShowScheduleForm(false)} className="rounded-sm text-sm">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled OSCEs List */}
      {schedules.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No OSCE examinations scheduled yet.</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Candidate</TableHead>
                <TableHead className="text-xs font-bold">Date & Time</TableHead>
                <TableHead className="text-xs font-bold">Location</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((osce) => (
                <TableRow key={osce.id}>
                  <TableCell>
                    <p className="font-medium text-[#1B2A4A]">{osce.candidate_name}</p>
                    <p className="text-xs text-slate-500">{osce.candidate_email}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{osce.exam_date ? new Date(osce.exam_date).toLocaleDateString() : 'TBD'}</p>
                    <p className="text-xs text-slate-500">{osce.exam_time || 'Time TBD'}</p>
                  </TableCell>
                  <TableCell className="text-sm">{osce.location || 'TBD'}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${
                      osce.status === 'completed' ? 'bg-green-100 text-green-700' :
                      osce.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {osce.status || 'scheduled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm"
                      onClick={() => {
                        setShowRescheduleModal(osce);
                        setRescheduleData({ new_date: '', new_time: '', reason: '' });
                      }}
                    >
                      <RefreshCcw className="h-3 w-3 mr-1" /> Reschedule
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRescheduleModal(null)}>
          <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-lg">Reschedule OSCE</CardTitle>
              <CardDescription>Rescheduling for {showRescheduleModal.candidate_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">New Date</Label>
                  <Input
                    type="date"
                    value={rescheduleData.new_date}
                    onChange={e => setRescheduleData({...rescheduleData, new_date: e.target.value})}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">New Time</Label>
                  <Input
                    type="time"
                    value={rescheduleData.new_time}
                    onChange={e => setRescheduleData({...rescheduleData, new_time: e.target.value})}
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Textarea
                  value={rescheduleData.reason}
                  onChange={e => setRescheduleData({...rescheduleData, reason: e.target.value})}
                  placeholder="Brief reason..."
                  className="rounded-sm mt-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleReschedule} className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm">
                  <RefreshCcw className="h-4 w-4 mr-1" /> Confirm Reschedule
                </Button>
                <Button variant="outline" onClick={() => setShowRescheduleModal(null)} className="rounded-sm">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Exam Centers Modal */}
      {showExamCentersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowExamCentersModal(false)}>
          <Card className="max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#C5A572]" />
                Approved Examination Centers
              </CardTitle>
              <CardDescription>List of approved centers where OSCE examinations can be conducted</CardDescription>
            </CardHeader>
            <CardContent className="p-0 max-h-[60vh] overflow-y-auto">
              {examCenters.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <MapPin className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No approved examination centers found.</p>
                  <p className="text-xs mt-1">Contact admin to add examination centers.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {examCenters.map((center) => (
                    <div key={center.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#1B2A4A]">{center.name}</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            {[center.address, center.city, center.state, center.country].filter(Boolean).join(', ') || 'Address not available'}
                          </p>
                          {center.email && (
                            <p className="text-xs text-slate-500 mt-1">Contact: {center.email}</p>
                          )}
                          {center.contact_person && (
                            <p className="text-xs text-slate-500">Contact Person: {center.contact_person}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm text-xs"
                          onClick={() => {
                            setScheduleForm({
                              ...scheduleForm,
                              location: [center.name, center.address, center.city].filter(Boolean).join(', ')
                            });
                            setShowExamCentersModal(false);
                            setShowScheduleForm(true);
                            toast.success('Location set. Now select a candidate and schedule the exam.');
                          }}
                        >
                          Use This Center
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="border-t p-4 bg-slate-50">
              <Button variant="outline" onClick={() => setShowExamCentersModal(false)} className="rounded-sm w-full">
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
